import type { GetServerSideProps } from 'next';
import { renderToString } from 'react-dom/server';
import {
  getServerState,
  InstantSearch,
  InstantSearchSSRProvider,
  useHits,
  usePagination,
} from 'react-instantsearch';

import { parseUiStateFilters } from '@/utils/algolia';
import { getLocaleFromParams } from '@/utils/locale';
import { extractPathFromUrl } from '@/utils/urlUtils';

import { Configure } from '@/components/Configure';
import {
  applyProductConfigurationFilters,
  buildUiStateFromURL,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { WatchHit } from '@/integrations/algolia/types';
import { PAGE_CONTEXT } from '@/integrations/algolia/types';
import { fetchListingPageData } from '@/integrations/contentful';
import { getUserTokenForAlgoliaSSR } from '@/lib/algoliaUserToken';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import {
  FILTERS_FETCH_LIMIT,
  HITS_COLLECTION_PER_PAGE,
  NEXT_PUBLIC_DEFAULT_APP_LOCALE,
} from '@/lib/const';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { capture404Event } from '@/lib/errors';
import type { LocaleMessages } from '@/lib/intl';
import { isSupportedLocale } from '@/lib/locales/locales';
import { PLP_ERRORS } from '@/lib/notFoundErrors';
import {
  getContentfulRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType, setCacheHeader } from '@/lib/revalidate';
import type { AllTheWatchesViewProps } from '@/views/AllTheWatchesView';
import { AllTheWatchesView } from '@/views/AllTheWatchesView';
import { fetchSelectedCollections } from '@/views/AllTheWatchesView/helpers';

export function DummyAllWatchesPage({
  draftMode,
  filters,
  indexName,
  userToken,
}: {
  draftMode?: boolean;
  filters: string;
  indexName: string;
  userToken?: string;
}) {
  const ssrClient = createSearchClient({
    isPreview: draftMode,
    isSsr: true,
    mockContext: PAGE_CONTEXT.PLP_WATCHES,
  });

  return (
    <InstantSearchSSRProvider>
      <InstantSearch
        indexName={indexName}
        insights={false}
        searchClient={ssrClient}
      >
        <Configure
          analyticsTags={['platform:web', 'product:watches', 'page:listing']}
          clickAnalytics={true}
          distinct={true}
          facetingAfterDistinct={true}
          facets={['*']}
          filters={filters}
          userToken={userToken}
        />
        <DummyHitsComponent />
        <DummyPaginationComponent />
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
}

function DummyHitsComponent() {
  const { hits } = useHits<WatchHit>();
  return <>{hits?.map((hit) => hit.objectID)}</>;
}

function DummyPaginationComponent() {
  // Register pagination widget to match client-side HitsPagination
  usePagination();
  return null;
}

export const getServerSideProps: GetServerSideProps<
  AllTheWatchesViewProps & {
    localeMessages: LocaleMessages;
  }
> = commonServerSideProps(async (context) => {
  setCacheHeader(CacheHeaderType.NO_CACHE_TAG, context);
  const { query, resolvedUrl } = context;
  const draftMode = resolveContentDraftMode(context);
  const routerLocale = getLocaleFromParams(context?.params);

  if (!isSupportedLocale(routerLocale)) {
    return { notFound: true };
  }

  const region = getRegionFromLocale(routerLocale);
  const contentfulLocale = region.locale.contentful ?? region.locale.locale;
  const indexName = getIndexName(
    'PRODUCT',
    routerLocale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  );
  const initialUiState = buildUiStateFromURL(
    resolvedUrl,
    indexName,
    undefined,
    undefined,
    region.locale,
  );

  const { countryCode } = getContentfulRegionFromLocale(routerLocale);

  const {
    algoliaCategoryId,
    cmsData: allTheWatchesCmsData,
    cmsDefaultLayoutData,
    seoConfigBuilder,
  } = await fetchListingPageData(
    'all the watches',
    contentfulLocale,
    null,
    null,
    null,
    countryCode,
    region.locale.code,
    draftMode,
  );

  const selectedCollectionsNames: string[] = await fetchSelectedCollections(
    allTheWatchesCmsData,
    region.country.channelSlug,
    region.locale.code,
  );

  const collectionFilters = selectedCollectionsNames
    ?.map((colName) => `collection:'${colName}'`)
    .join(' OR ');

  let algoliaFilters = applyProductConfigurationFilters(
    ProductConfigurationFilterType.PLP,
    'Watch',
    draftMode,
    collectionFilters,
  );

  const algoliaCategoryIdIsNotNullOrEmpty =
    algoliaCategoryId && algoliaCategoryId !== '';

  if (algoliaCategoryIdIsNotNullOrEmpty) {
    algoliaFilters =
      algoliaFilters + ` AND category_page_id:'${algoliaCategoryId}'`;
  }

  const ssrClient = createSearchClient({
    isSsr: true,
    isPreview: draftMode,
    mockContext: PAGE_CONTEXT.PLP_WATCHES,
  });

  const index = ssrClient.initIndex(indexName);

  // Perform a search request to get nbPages
  const searchResponse = await index.search('', {
    filters: algoliaFilters,
    facets: ['*'],
    maxValuesPerFacet: FILTERS_FETCH_LIMIT,
    hitsPerPage: HITS_COLLECTION_PER_PAGE,
  });

  const pageIndex = query?.page && parseInt(query.page as string);
  if (
    typeof pageIndex === 'number' &&
    (pageIndex > searchResponse.nbPages || pageIndex < 1)
  ) {
    capture404Event('All Watches PLP', {
      requestUrl: context.req.url ? extractPathFromUrl(context.req.url) : '',
      reason: PLP_ERRORS.PLP_PAGINATION_OUT_OF_RANGE,
    });
    return {
      notFound: true,
    };
  }

  // Translate URL filter values from English to the target locale using
  // searchResponse facets, so that the server-side Algolia search uses the
  // correct locale-specific values (e.g. case.ES:"Acero inoxidable | STSTL"
  // instead of case.ES:"Stainless steel | STSTL").
  const translatedUiState = buildUiStateFromURL(
    resolvedUrl,
    indexName,
    undefined,
    undefined,
    region.locale,
    {
      initialResults: {
        [indexName]: {
          results: [
            {
              facets: searchResponse.facets,
              renderingContent: searchResponse.renderingContent,
            },
          ],
        },
      },
    } as any,
    'EN_US',
  );

  // Convert translated URL query parameters to Algolia filters
  const urlFilters = parseUiStateFilters(translatedUiState[indexName]);

  // Combine collection filters with URL filters
  const combinedFilters = urlFilters
    ? algoliaFilters
      ? `${algoliaFilters} AND ${urlFilters}`
      : urlFilters
    : algoliaFilters;

  // Generate Algolia user token server-side for consistent SSR/client rendering
  const algoliaUserToken = getUserTokenForAlgoliaSSR(context.req) ?? null;

  const serverState = await getServerState(
    <DummyAllWatchesPage
      draftMode={draftMode}
      filters={combinedFilters}
      indexName={indexName}
      userToken={algoliaUserToken ?? undefined}
      {...(typeof pageIndex === 'number' ? { page: pageIndex - 1 } : {})}
    />,
    {
      renderToString,
    },
  );

  // Inject facets from the unfiltered search into serverState for translation
  // This ensures facets are available even when URL filters don't match any products
  if (serverState.initialResults?.[indexName]?.results?.[0]) {
    serverState.initialResults[indexName].results[0].facets =
      searchResponse.facets;
  }

  return {
    props: {
      algoliaFilters,
      algoliaUserToken,
      draftMode: draftMode ?? false,
      instantSearchProps: {
        initialUiState,
        indexName,
        serverState,
      },
      allTheWatchesCmsData,
      cmsDefaultLayoutData,
      seoConfig: seoConfigBuilder?.build(),
      serverState,
    },
  };
});

export default AllTheWatchesView;
